import type React from 'react'

import { FEATURE_GROUPS, type FeatureGroupId } from 'features/customers/constants/featureGroups'
import type { FeatureKind } from 'features/customers/constants/features'
import type { FeatureChange, FeatureChangeLog } from 'features/customers/hooks/useCustomerFeatureChanges'
import {
  featureGroupOf,
  featureKind,
  getFeatureDescription,
  getFeatureLabel,
  isFeatureManaged,
} from 'features/customers/utils/features'
import isEqual from 'lodash/isEqual'

import { EChangeSource } from '@albacross/types'

export type FeatureRow = {
  name: string
  label: string
  // Absent for a key the registry does not declare, which only reaches here via the change log.
  description?: string
  value: unknown
  planValue: unknown
  kind: FeatureKind
  // `overridden` is stored on the customer, whatever the value; `differs` is not what the plan gives.
  overridden: boolean
  differs: boolean
  editable: boolean
  setBy?: FeatureChange
  history: FeatureChange[]
}

export type FeatureRowGroup = {
  id: FeatureGroupId
  title: string
  startsOpen: boolean
  icon: React.ReactNode
  color: string
  rows: FeatureRow[]
  overridden: number
  differing: number
}

export type FeatureFilter = 'all' | 'overridden' | 'differs' | 'person'

type FeatureDatum = { name: string; feature: unknown; planFeature: unknown }

export const setByPerson = (row: FeatureRow) => row.setBy?.source === EChangeSource.Admin

const MATCHES: Record<FeatureFilter, (row: FeatureRow) => boolean> = {
  all: () => true,
  overridden: row => row.overridden,
  differs: row => row.differs,
  person: setByPerson,
}

const historyByKey = (changes: FeatureChange[]) => {
  const byKey = new Map<string, FeatureChange[]>()

  for (const change of changes) {
    const existing = byKey.get(change.feature_key)

    if (existing) existing.push(change)
    else byKey.set(change.feature_key, [change])
  }

  return byKey
}

// Recording never throws into a save, so a save can land with no row - and the newest record then
// describes an older value. Naming its author would put someone else's change on them.
const attributableTo = (latest: FeatureChange | undefined, overridden: boolean, value: unknown) =>
  overridden && latest && latest.new_value !== null && isEqual(latest.new_value, value) ? latest : undefined

export const buildFeatureRows = (
  data: FeatureDatum[],
  log: FeatureChangeLog,
  filter: FeatureFilter = 'all',
): FeatureRowGroup[] => {
  // Deploys are dispatched in parallel, so management can be live against a customers build still
  // returning the pre-DEV-12408 array. Destructuring that throws and the boundary takes Bunia down.
  const { changes, overridden } = Array.isArray(log) ? { changes: log, overridden: [] } : log

  const history = historyByKey(changes)
  const overriddenKeys = new Set(overridden)

  // An undeclared key leaves `features` when it is reset, taking the history that explains why.
  const known = new Set(data.map(datum => datum.name))
  const orphaned = [...new Set(changes.map(change => change.feature_key))]
    .filter(key => !known.has(key))
    .map(name => ({ name, feature: undefined, planFeature: undefined }))

  const rows = [...data, ...orphaned].map(({ name, feature, planFeature }): FeatureRow => {
    const keyHistory = history.get(name) ?? []
    const differs = !isEqual(feature, planFeature)
    const overridden = overriddenKeys.has(name)

    return {
      name,
      label: getFeatureLabel(name) || name,
      description: getFeatureDescription(name),
      value: feature,
      planValue: planFeature,
      kind: featureKind(name, feature, planFeature),
      // Not "differs": getCustomerFeatures merges in values nobody stored, such as a trial's provider.
      overridden,
      differs,
      editable: isFeatureManaged(name),
      setBy: attributableTo(keyHistory[0], overridden, feature),
      history: keyHistory,
    }
  })

  const matches = MATCHES[filter]

  return FEATURE_GROUPS.map(group => {
    const visible = rows.filter(row => featureGroupOf(row.name) === group.id && matches(row))

    return {
      id: group.id,
      title: group.title,
      startsOpen: group.startsOpen,
      icon: group.icon,
      color: group.color,
      rows: visible,
      overridden: visible.filter(row => row.overridden).length,
      differing: visible.filter(row => row.differs).length,
    }
    // Quota lines have no plan value and no actor, so no narrowing filter can honestly match them.
  }).filter(group => group.rows.length > 0)
}

export const filterCounts = (rows: FeatureRow[]): Record<FeatureFilter, number> => ({
  all: rows.length,
  overridden: rows.filter(MATCHES.overridden).length,
  differs: rows.filter(MATCHES.differs).length,
  person: rows.filter(MATCHES.person).length,
})
